import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Stack,
  CircularProgress,
  Alert,
} from "@mui/material";
import ViewKanbanOutlinedIcon from "@mui/icons-material/ViewKanbanOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { useNavigate } from "react-router-dom";
import { useTeamBoardForClient } from "../../hooks/useTeamBoards";
import { clientFormSectionBodySx, clientFormSectionHeaderSx } from "./clientFormStyles";
import { getTasksBasePath, TEAM_BASE } from "../../utils/teamNavigation";

/**
 * Acesso interno ao painel de tarefas do cliente (não aparece na lista de Painéis).
 */
export default function ClientTaskBoardSection({
  clientId,
  sectionCardSx,
  embeddedInTab = false,
}) {
  const navigate = useNavigate();
  const enabled = Boolean(clientId);
  const { data: board, isLoading, isError, error, refetch, isFetching } = useTeamBoardForClient(
    clientId,
    { enabled }
  );

  const tasksBase = getTasksBasePath(TEAM_BASE);
  const openCount = (board?.columns || []).reduce(
    (acc, col) => acc + (col.cards || []).filter((c) => !c.completedAt && !col.isDone).length,
    0
  );
  const totalCount = (board?.columns || []).reduce(
    (acc, col) => acc + (col.cards || []).length,
    0
  );

  const body = (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        Painel de tarefas vinculado a este cliente. A equipe acompanha o consolidado em
        &quot;Tarefas de clientes&quot;; o cliente vê o kanban no portal.
      </Typography>

      {!enabled ? (
        <Alert severity="info">Salve o cadastro para abrir o painel de tarefas.</Alert>
      ) : null}

      {enabled && isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={28} />
        </Box>
      ) : null}

      {enabled && isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()} disabled={isFetching}>
              Tentar de novo
            </Button>
          }
        >
          {error?.message || "Não foi possível carregar o painel de tarefas."}
        </Alert>
      ) : null}

      {enabled && board?.id ? (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
        >
          <Typography variant="body2" fontWeight={700}>
            {totalCount} tarefa{totalCount === 1 ? "" : "s"}
            {openCount > 0 ? ` · ${openCount} em aberto` : ""}
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<OpenInNewOutlinedIcon />}
            onClick={() => navigate(`${tasksBase}/client/${clientId}`)}
          >
            Abrir painel
          </Button>
        </Stack>
      ) : null}
    </Stack>
  );

  if (embeddedInTab) {
    return <Box sx={clientFormSectionBodySx}>{body}</Box>;
  }

  return (
    <Card sx={sectionCardSx} elevation={0} variant="outlined">
      <CardHeader
        avatar={<ViewKanbanOutlinedIcon color="primary" />}
        title="Painel de tarefas"
        subheader="Origem das demandas acompanhadas com o cliente"
        sx={clientFormSectionHeaderSx}
      />
      <CardContent sx={clientFormSectionBodySx}>{body}</CardContent>
    </Card>
  );
}
